
import { createToolbarEvent,
    sendAnalytics
} from '../../../analytics';
import { openDialog } from '../../../base/dialog';
import { IconToggleRecording } from '../../../base/icons';
import { MHRecordingConstants } from '../../../base/lib-meet-hour';
import {
    getLocalParticipant,
    isLocalParticipantModerator
} from '../../../base/participants/functions';
import { AbstractButton, type AbstractButtonProps } from '../../../base/toolbox/components';
import { showErrorNotification, showNotification } from '../../../notifications';
import { getActiveSession } from '../../functions';

/**
 * The type of the React {@code Component} props of
 * {@link AbstractRecordButton}.
 */
export type Props = AbstractButtonProps & {

    /**
     * True if the button needs to be disabled.
     */
    _disabled: Boolean,

    /**
     * True if there is a running active recording, false otherwise.
     */
    _isRecordingRunning: boolean,

    /**
     * The tooltip to display when hovering over the button.
     */
    _tooltip: ?String,

    /**
     *
     */
    _isRecordingandLivestreamingOn: ?Boolean,

    /**
     * The redux {@code dispatch} function.
     */
    dispatch: Function,

    /**
     * The i18n translate function.
     */
    t: Function
};

/**
 * An abstract implementation of a button for starting and stopping recording.
 */
export default class AbstractRecordButton<P: Props> extends AbstractButton<P, *> {
    accessibilityLabel = 'toolbar.accessibilityLabel.recording';
    icon = IconToggleRecording;

    /**
     *
     * Constructor.
     *
     * @param {*} props - Props of the class.
     *
     */
    constructor(props: P) {
        super(props);

        this.label = 'dialog.startRecording';
        this.toggledLabel = 'dialog.stopRecording';
    }

    /**
     * Returns the tooltip that should be displayed when the button is disabled.
     *
     * @private
     * @returns {string}
     */
    _getTooltip() {
        return this.props._tooltip || '';
    }

    /**
     * Handles clicking / pressing the button.
     *
     * @override
     * @protected
     * @returns {void}
     */
    _handleClick() {
        const { _isRecordingRunning, _isRecordingandLivestreamingOn, dispatch } = this.props;
        const { StartRecordingDialog, StopRecordingDialog } = require('./_');

        if (_isRecordingandLivestreamingOn) {
            dispatch(showNotification({
                titleKey: 'dialog.recording',
                descriptionKey: 'dialog.startRecordingWarning'
            }, 3000));

            return;
        }

        if (this.props._disabled) {
            dispatch(showErrorNotification({
                titleKey: 'dialog.recordingDisabledBecauseOfActiveLiveStreamingTooltip'
            }));
        } else {
            sendAnalytics(createToolbarEvent(
                'recording.button',
                {
                    'is_recording': _isRecordingRunning,
                    type: MHRecordingConstants.mode.FILE
                }));

            dispatch(openDialog(
                    _isRecordingRunning ? StopRecordingDialog : StartRecordingDialog
            ));

        }
    }

    /**
     * Helper function to be implemented by subclasses, which must return a
     * boolean value indicating if this button is disabled or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isDisabled() {
        return this.props._isRecordingandLivestreamingOn || this.props._disabled || this.props._isLiveStreamRunning;
    }

    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._isRecordingRunning && !this.props._isRecordingandLivestreamingOn;
    }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code RecordButton} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Props} ownProps - The own props of the Component.
 * @private
 * @returns {{
 *     _disabled: boolean,
 *     _isRecordingRunning: boolean,
 *     visible: boolean
 * }}
 */
export function _mapStateToProps(state: Object, ownProps: Props): Object {
    let { visible } = ownProps;
    const { confSettings } = state['features/base/jwt'];

    // a button can be disabled/enabled if enableFeaturesBasedOnToken
    // is on or if the livestreaming is running.
    let _disabled;
    let _tooltip = '';
    const recSettings = confSettings ? confSettings?.ENABLE_RECORDING === 1 : false;

    if (typeof visible === 'undefined') {
        // If the containing component provides the visible prop, that is one
        // above all, but if not, the button should be autonomus and decide on
        // its own to be visible or not.
        const isModerator = isLocalParticipantModerator(state);
        const {
            enableFeaturesBasedOnToken,
            fileRecordingsEnabled
        } = state['features/base/config'];
        const { features = {} } = getLocalParticipant(state);

        visible = isModerator && (fileRecordingsEnabled || recSettings);

        if (enableFeaturesBasedOnToken) {
            visible = visible && String(features.recording) === 'true';
            _disabled = String(features.recording) === 'disabled';
            if (!visible && !_disabled) {
                _disabled = true;
                visible = true;
                _tooltip = 'dialog.recordingDisabledTooltip';
            }
        }
    }

    const { isRecordingandLivestreamingOn = false } = state['features/base/mtApi'];

    // disable the button if the livestreaming is running.
    if (getActiveSession(state, MHRecordingConstants.mode.STREAM) && !isRecordingandLivestreamingOn) {
        _disabled = true;
        _tooltip = 'dialog.recordingDisabledBecauseOfActiveLiveStreamingTooltip';
    }

    return {
        _disabled,
        _isRecordingRunning: Boolean(getActiveSession(state, MHRecordingConstants.mode.FILE)),
        _isLiveStreamRunning: Boolean(getActiveSession(state, MHRecordingConstants.mode.STREAM)),
        _isRecordingandLivestreamingOn: isRecordingandLivestreamingOn,
        _tooltip,
        visible
    };
}
